import type { PaneComponentProps, PaneRegistry } from "@hq/layout";
import {
  AnytimeView,
  CompletedView,
  InboxView,
  SomedayView,
  TodayView,
  UpcomingView,
} from "@hq/tasks-ui";
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  FileText,
  Folder,
  Inbox,
  LayoutTemplate,
  LayoutDashboard,
  ListChecks,
  Moon,
  Settings,
  StickyNote,
  Sun,
  Trash2,
} from "lucide-react";
import { AllNotesPane } from "../panes/AllNotesPane";
import { DashboardsPane } from "../panes/DashboardsPane";
import { CalendarPane } from "../panes/CalendarPane";
import { DailyPane } from "../panes/DailyPane";
import { NotePane } from "../panes/NotePane";
import { ProjectPane } from "../panes/ProjectPane";
import { SettingsPane } from "../panes/SettingsPane";
import { TaskPane } from "../panes/TaskPane";
import { TemplatesPane } from "../panes/TemplatesPane";
import { TrashPane } from "../panes/TrashPane";
import { isPreview } from "./model";
import { useWorkspace } from "./WorkspaceContext";

function TaskViewPane(props: {
  view: "today" | "inbox" | "anytime" | "upcoming" | "someday" | "completed";
}) {
  const ws = useWorkspace();
  const common = { onOpenTask: ws.openTask, className: "h-full overflow-y-auto px-6 py-7" };
  switch (props.view) {
    case "today":
      return <TodayView {...common} />;
    case "anytime":
      return <AnytimeView {...common} />;
    case "inbox":
      return <InboxView {...common} />;
    case "upcoming":
      return <UpcomingView {...common} />;
    case "someday":
      return <SomedayView {...common} />;
    case "completed":
      return <CompletedView {...common} onGoToToday={() => ws.open({ paneType: "today" })} />;
  }
}

const view = (name: "today" | "inbox" | "anytime" | "upcoming" | "someday" | "completed") =>
  function ViewPane(_props: PaneComponentProps) {
    return <TaskViewPane view={name} />;
  };

const previewTab = (pane: Parameters<typeof isPreview>[0]) =>
  isPreview(pane) ? "italic" : undefined;

/** `paneType` → component. This is the only place feature packages meet the layout. */
export const paneRegistry: PaneRegistry = {
  today: { component: view("today"), title: () => "Today", icon: Sun },
  anytime: { component: view("anytime"), title: () => "Anytime", icon: ListChecks },
  inbox: { component: view("inbox"), title: () => "Inbox", icon: Inbox },
  upcoming: { component: view("upcoming"), title: () => "Upcoming", icon: CalendarDays },
  someday: { component: view("someday"), title: () => "Someday", icon: Moon },
  completed: { component: view("completed"), title: () => "Completed", icon: CheckCircle2 },
  project: { component: ProjectPane, title: (p) => p.title ?? "Project", icon: ListChecks },
  daily: { component: DailyPane, title: () => "Daily Notes", icon: BookOpen },
  allNotes: { component: AllNotesPane, title: () => "All Notes", icon: StickyNote },
  folder: { component: AllNotesPane, title: (p) => p.title ?? "Folder", icon: Folder },
  templates: { component: TemplatesPane, title: () => "Templates", icon: LayoutTemplate },
  note: {
    component: NotePane,
    title: (p) => p.title ?? "Note",
    icon: FileText,
    tabClassName: previewTab,
  },
  task: {
    component: TaskPane,
    title: (p) => p.title ?? "Task",
    icon: CheckCircle2,
    tabClassName: previewTab,
  },
  dashboards: { component: DashboardsPane, title: () => "Dashboards", icon: LayoutDashboard },
  calendar: { component: CalendarPane, title: () => "Calendar", icon: CalendarDays },
  trash: { component: TrashPane, title: () => "Trash", icon: Trash2 },
  settings: { component: SettingsPane, title: () => "Settings", icon: Settings },
};
